// @ts-check

/**
 * @file Renders invoices linked to an invoice group.
 */

import {useLocalSearchParams} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import InvoiceCard from "../../../src/components/invoices/card"
import {InvoiceGroupLink} from "../../../src/frontend-models/invoice-group-link"
import {InvoiceGroup} from "../../../src/frontend-models/invoice-group"
import Link from "../../../src/components/link"
import {routePathWithParams} from "../../../src/screens/invoice-groups/shared"
import errorMessage from "../../../src/shared/error-message"
import {requiredRouteParam} from "../../../src/shared/route-params"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import ScreenTitle from "../../../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}
const INVOICE_CARD_PRELOAD_SELECT = {
  Invoice: ["amountCents", "amountCurrency", "creditorId", "date", "id", "invoiceNo", "invoiceType", "organizationId", "state", "title", "translatedInvoiceType", "translatedState"],
  InvoiceGroupLink: ["id", "invoiceGroupId", "invoiceId"],
  Organization: ["id", "name"]
}

/** @typedef {import("../../../src/frontend-models/invoice").default} Invoice */
/** @typedef {{id: string, name: string}} ScreenInvoiceGroup */
/** @typedef {"forbidden" | "invoice_group_not_found" | "missing_invoice_group_id" | "not_signed_in"} InvoiceGroupInvoicesErrorType */

/**
 * @typedef {object} InvoiceGroupInvoicesState
 * @property {string | null} errorText Load error text.
 * @property {ScreenInvoiceGroup | null} invoiceGroup Loaded invoice group.
 * @property {Invoice[]} invoices Loaded linked invoices.
 * @property {boolean} loading Whether invoices are loading.
 */

/** @typedef {{[key: string]: unknown}} InvoiceGroupInvoicesScreenProps */
/** @typedef {InvoiceGroupInvoicesState} InvoiceGroupInvoicesScreenState */

/**
 * Invoice-group invoices screen migrated from the legacy Rails/React app.
 * @augments {ShapeComponent<InvoiceGroupInvoicesScreenProps, InvoiceGroupInvoicesScreenState>}
 */
class InvoiceGroupInvoicesScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string} */
  invoiceGroupID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {InvoiceGroupInvoicesScreenState} */
  state = {
    errorText: null,
    invoiceGroup: null,
    invoices: [],
    loading: true
  }
  loadRequestId = 0

  /** Initializes route params and invoice loading. */
  setup() {
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()

    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()

    useScreenLoading(this.s.loading)
    const invoiceGroupID = requiredRouteParam(params, "id", "Invoice group ID")
    this.invoiceGroupID = invoiceGroupID
    this.params = params
    useEffect(() => {
      void this.loadInvoices()
    }, [invoiceGroupID])
  }

  /**
   * Renders linked invoices for the invoice group.
   * @returns {ReactNode} Invoice group invoices content.
   */
  render() {
    if (this.s.loading) {
      return (
        <View
          style={styles.loadingScreen ||= {
            flex: 1,
            padding: 24
          }}
          testID="invoiceGroupInvoicesScreen"
        >
          <Text
            style={styles[`loadingText-${this.tt.themeName}`] ||= {
              fontSize: 16,
              fontWeight: 600
            }}
            testID="invoiceGroupInvoicesScreen/loadingText"
          >
            {_("Loading invoices...")}
          </Text>
        </View>
      )
    }
    const screenTitle = this.s.invoiceGroup?.name || _("Invoice group")
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        contentContainerStyle={styles.contentContainer ||= {
          padding: 24
        }}
        testID="invoiceGroupInvoicesScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <Text
          style={styles[`title-${this.tt.themeName}`] ||= {
            fontSize: 28,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="invoiceGroupInvoicesScreen/title"
        >
          {screenTitle}
        </Text>
        <View
          style={styles.navBar ||= {
            flexDirection: "row",
            gap: 12,
            marginBottom: 24
          }}
          testID="invoiceGroupInvoicesScreen/navBar"
        >
          <Link
            href={routePathWithParams(this.tt.params, `/invoice-groups/${encodeURIComponent(this.tt.invoiceGroupID)}`)}
            style={styles[`navButton-${this.tt.themeName}`] ||= {
              backgroundColor: this.tt.colors.surfaceMuted,
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 600,
              paddingHorizontal: 16,
              paddingVertical: 10
            }}
            testID="invoiceGroupInvoicesScreen/generalTab"
          >
            {_("General")}
          </Link>
          <View
            style={styles[`activeNavButton-${this.tt.themeName}`] ||= {
              backgroundColor: this.tt.colors.primary,
              borderRadius: 999,
              paddingHorizontal: 16,
              paddingVertical: 10
            }}
            testID="invoiceGroupInvoicesScreen/invoicesTab"
          >
            <Text
              style={styles[`activeNavButtonText-${this.tt.themeName}`] ||= {
                color: this.tt.colors.onPrimary,
                fontSize: 14,
                fontWeight: 600
              }}
              testID="invoiceGroupInvoicesScreen/invoicesTab/text"
            >
              {_("Invoices")}
            </Text>
          </View>
        </View>
        {this.s.errorText &&
          <View
            style={styles[`errorCard-${this.tt.themeName}`] ||= {
              backgroundColor: this.tt.colors.dangerBackground,
              borderColor: this.tt.colors.danger,
              borderRadius: 12,
              borderWidth: 1,
              padding: 16
            }}
            testID="invoiceGroupInvoicesScreen/errorCard"
          >
            <Text
              style={styles[`errorText-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontSize: 15,
                fontWeight: 600
              }}
              testID="invoiceGroupInvoicesScreen/errorText"
            >
              {this.s.errorText}
            </Text>
          </View>
        }
        {!this.s.errorText && this.s.invoices.length === 0 &&
          <Text
            style={styles[`emptyText-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontSize: 15
            }}
            testID="invoiceGroupInvoicesScreen/emptyText"
          >
            {_("No invoices were linked to this invoice group")}
          </Text>
        }
        {!this.s.errorText &&
          this.s.invoices.map((invoice) => (
            <InvoiceCard
              key={invoice.id()}
              invoice={invoice}
              testID={`invoiceGroupInvoicesScreen/row-${invoice.id()}`}
            />
          ))}
      </View>
    )
  }

  /**
   * Loads invoice group data and linked invoices.
   * @returns {Promise<void>} Resolves when invoice loading completes.
   */
  async loadInvoices() {
    const requestId = ++this.loadRequestId
    if (!this.tt.invoiceGroupID) {
      this.s.errorText = _("Invoice group id is missing")
      this.s.invoiceGroup = null
      this.s.invoices = []
      this.s.loading = false
      return
    }
    try {
      this.s.errorText = null
      this.s.loading = true
      const response =
        /** @type {{invoiceGroup?: {id?: unknown, name?: unknown}}} */
        (await InvoiceGroup.invoicesRoute({id: this.tt.invoiceGroupID}))
      const invoiceGroup = response.invoiceGroup
      const invoiceGroupID = invoiceGroup?.id
      const invoiceGroupName = invoiceGroup?.name

      if (
        !invoiceGroup ||
        typeof invoiceGroupID !== "string" ||
        typeof invoiceGroupName !== "string"
      ) {
        throw new Error("Invoice group invoices route response was incomplete")
      }

      const loadedInvoiceGroup = {
        id: invoiceGroupID,
        name: invoiceGroupName
      }

      if (requestId !== this.loadRequestId) {
        return
      }
      const links = await InvoiceGroupLink
        .where({
          invoiceGroupId: this.tt.invoiceGroupID
        })
        .preload({invoice: {creditor: true, organization: true}})
        .select(INVOICE_CARD_PRELOAD_SELECT)
        .toArray()
      if (requestId !== this.loadRequestId) {
        return
      }
      this.s.errorText = null
      this.s.invoiceGroup = loadedInvoiceGroup
      this.s.invoices = links
        .map((link) => {
          const invoice = link.invoice()
          if (!invoice) {
            return null
          }
          return invoice
        })
        .filter(
          /**
           * Keeps loaded invoice rows.
           * @param {Invoice | null} invoice Invoice row candidate.
           * @returns {invoice is Invoice} True when invoice row is loaded.
           */
          (invoice) =>
            invoice !== null
        )
      this.s.loading = false
    } catch (error) {
      if (requestId !== this.loadRequestId) {
        return
      }
      this.s.errorText = this.errorTextFromError(error)
      this.s.invoiceGroup = null
      this.s.invoices = []
      this.s.loading = false
    }
  }

  /**
   * Converts a loading error into translated screen text.
   * @param {unknown} error Error value to translate.
   * @returns {string} User-facing error text.
   */
  errorTextFromError(error) {
    const errorType = velociousErrorType(error)

    if (errorType === "not_signed_in") {
      return _("You must sign in to continue")
    }
    if (errorType === "forbidden") {
      return _("You do not have access to this page")
    }
    if (errorType === "invoice_group_not_found") {
      return _("Invoice group was not found")
    }
    if (errorType === "missing_invoice_group_id") {
      return _("Invoice group id is missing")
    }

    return `${_("Could not load invoices")}: ${errorMessage(error)}`
  }

}

/** @type {NamedExoticComponent<InvoiceGroupInvoicesScreenProps>} */
const InvoiceGroupInvoicesScreenComponent = memo(shapeComponent(InvoiceGroupInvoicesScreen))

export default InvoiceGroupInvoicesScreenComponent

/**
 * Reads Velocious structured error metadata from a thrown frontend-model error.
 * @param {unknown} error Error value.
 * @returns {InvoiceGroupInvoicesErrorType | null} Error type or null.
 */
function velociousErrorType(error) {
  if (!error || typeof error !== "object") {
    return null
  }

  const errorWithMetadata =
    /** @type {{velocious?: {errorType?: unknown}}} */
    (error)
  const velocious = errorWithMetadata.velocious

  if (!velocious || typeof velocious.errorType !== "string") {
    return null
  }

  if (
    velocious.errorType === "forbidden" ||
    velocious.errorType === "invoice_group_not_found" ||
    velocious.errorType === "missing_invoice_group_id" ||
    velocious.errorType === "not_signed_in"
  ) {
    return velocious.errorType
  }

  return null
}
